import React, { useState, useEffect } from "react";
import layoutStyles from "../PanelUser/PanelLayout.module.css";
import styles from "./MiPerfil.module.css";
import axiosInstance from "../../api/axiosConfig";
import {
  PanelSidebar,
  PanelHeader,
  MOCK_USER,
  getInitialsFrom,
  formatDate,
} from "../PanelUser/PanelShared";

/* -------- helpers -------- */
function calcAge(birthdate) {
  if (!birthdate) return "—";
  const hoy = new Date();
  const nac = new Date(birthdate);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (
    hoy.getMonth() < nac.getMonth() ||
    (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())
  )
    edad--;
  return `${edad} años`;
}

function calcProgress(user) {
  const fields = [
    "name",
    "lastName",
    "dni",
    "genre",
    "birthdate",
    "email",
    "phone",
    "department",
    "district",
    "address",
  ];
  const filled = fields.filter((f) => user?.[f]).length;
  return Math.round((filled / fields.length) * 100);
}

const TABS = ["Datos personales", "Seguridad", "Notificaciones"];

/* ======================================
   COMPONENTE PRINCIPAL
====================================== */
export default function MiPerfil() {
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axiosInstance
        .get("/api/datapersonal")
        .then((r) => setUser(r.data))
        .catch(() => setUser(MOCK_USER));
    } else {
      setUser(MOCK_USER);
    }
  }, []);

  const handleEdit = () => {
    setFormData({
      name: user?.name || "",
      lastName: user?.lastName || "",
      dni: user?.dni || "",
      genre: user?.genre || "",
      birthdate: user?.birthdate ? user.birthdate.split("T")[0] : "",
      email: user?.email || "",
      phone: user?.phone || "",
      department: user?.department || "",
      district: user?.district || "",
      address: user?.address || "",
    });
    setEditing(true);
  };

  const handleCancel = () => setEditing(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axiosInstance.put(`/api/user/${user.id}`, formData);
      setUser((prev) => ({ ...prev, ...response.data?.data, ...formData }));
      setEditing(false);
    } catch (err) {
      console.error("Error al guardar perfil:", err);
    } finally {
      setSaving(false);
    }
  };

  const progress = calcProgress(user);
  const displayUser = user || MOCK_USER;

  return (
    <div className={layoutStyles.panelPage}>
      <PanelSidebar user={displayUser} />

      <main className={layoutStyles.main}>
        <PanelHeader label="Mi panel" title="Mi perfil" user={displayUser} />
        <p className={styles.subtitle}>
          Gestioná tu información personal y preferencias de cuenta.
        </p>

        {/* ── Tarjeta de resumen de perfil ── */}
        <div className={layoutStyles.card} style={{ marginBottom: 20 }}>
          <div className={styles.profileSummary}>
            <div className={styles.summaryLeft}>
              <div className={styles.avatarLg}>
                {getInitialsFrom(displayUser.name, displayUser.lastName)}
              </div>
              <div>
                <p className={styles.summaryName}>
                  {displayUser.name} {displayUser.lastName}
                </p>
                <p className={styles.summaryEmail}>{displayUser.email}</p>
                <div className={styles.progressWrap}>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                  </div>
                  <span className={styles.progressLabel}>{progress}% completo</span>
                </div>
              </div>
            </div>
            <div>
              {editing ? (
                <button className={styles.btnCancelarTop} onClick={handleCancel}>
                  ✕ Cancelar
                </button>
              ) : (
                <button className={styles.btnEditar} onClick={handleEdit}>
                  Editar perfil
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className={styles.tabs}>
          {TABS.map((t, i) => (
            <button
              key={t}
              className={`${styles.tab} ${activeTab === i ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(i)}
            >
              {t}
            </button>
          ))}
        </div>

        {/* ── Contenido de tabs ── */}
        {activeTab === 0 && (
          <div className={layoutStyles.card}>
            {editing ? (
              /* ── MODO EDICIÓN ── */
              <>
                <div className={styles.dataGrid}>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>DNI</label>
                    <input
                      className={styles.fieldInput}
                      name="dni"
                      value={formData.dni}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Género</label>
                    <select
                      className={styles.fieldInput}
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                    >
                      <option value="">Seleccionar</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Femenino">Femenino</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Nombres</label>
                    <input
                      className={styles.fieldInput}
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Apellidos</label>
                    <input
                      className={styles.fieldInput}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Fecha de nacimiento</label>
                    <input
                      className={styles.fieldInput}
                      type="date"
                      name="birthdate"
                      value={formData.birthdate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Edad</label>
                    <input
                      className={styles.fieldInput}
                      value={calcAge(formData.birthdate)}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Correo electrónico</label>
                    <input
                      className={styles.fieldInput}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Teléfono</label>
                    <input
                      className={styles.fieldInput}
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Departamento</label>
                    <input
                      className={styles.fieldInput}
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Distrito</label>
                    <input
                      className={styles.fieldInput}
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={`${styles.fieldGroup} ${styles.fieldFull}`}>
                    <label className={styles.fieldLabel}>Dirección</label>
                    <input
                      className={styles.fieldInput}
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className={styles.editActions}>
                  <button className={styles.btnCancelarBottom} onClick={handleCancel}>
                    Cancelar
                  </button>
                  <button className={styles.btnGuardar} onClick={handleSave} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </>
            ) : (
              /* ── MODO VISTA ── */
              <div className={styles.dataGrid}>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>DNI</p>
                  <p className={styles.fieldValue}>{displayUser.dni || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Género</p>
                  <p className={styles.fieldValue}>{displayUser.genre || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Nombres</p>
                  <p className={styles.fieldValue}>{displayUser.name || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Apellidos</p>
                  <p className={styles.fieldValue}>{displayUser.lastName || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Fecha de nacimiento</p>
                  <p className={styles.fieldValue}>{formatDate(displayUser.birthdate)}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Edad</p>
                  <p className={styles.fieldValue}>{calcAge(displayUser.birthdate)}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Correo electrónico</p>
                  <p className={styles.fieldValue}>{displayUser.email || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Teléfono</p>
                  <p className={styles.fieldValue}>{displayUser.phone || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Departamento</p>
                  <p className={styles.fieldValue}>{displayUser.department || "—"}</p>
                </div>
                <div className={styles.fieldGroup}>
                  <p className={styles.fieldLabel}>Distrito</p>
                  <p className={styles.fieldValue}>{displayUser.district || "—"}</p>
                </div>
                <div className={`${styles.fieldGroup} ${styles.fieldFull}`}>
                  <p className={styles.fieldLabel}>Dirección</p>
                  <p className={styles.fieldValue}>{displayUser.address || "—"}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div className={layoutStyles.card}>
            <p className={layoutStyles.emptyState}>Cambio de contraseña próximamente.</p>
          </div>
        )}

        {activeTab === 2 && (
          <div className={layoutStyles.card}>
            <p className={layoutStyles.emptyState}>Preferencias de notificaciones próximamente.</p>
          </div>
        )}
      </main>
    </div>
  );
}
